import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dpsPrivApi, dpsRegDoc, dpsSubmissions,
  type DpsPrivIncoming, type DpsRegDoc, type DpsSubmission,
} from '@/api/dpsSubmissions';
import { dpsRequests } from '@/api/dpsRequests';
import { taxReportJ05 } from '@/api/taxReportJ05';
import { hireNotifications } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DatePopover from '@/components/DatePopover';
import KeyAuthPanel from '@/shared/eusign/KeyAuthPanel';
import DpsReceiptBlank from '@/shared/eusign/DpsReceiptBlank';
import {
  activateKey, buildSignedContainer, bytesToBase64, developReceiptToXml,
  encryptReportIdVariants, keyCode, listKeys, parseReceiptFieldsFromXml,
  signApiAuthHeader,
} from '@/shared/eusign/eusign';
import type {
  KeyOwnerInfo, ReceiptFields, StoredKey,
} from '@/shared/eusign/eusign';

/**
 * Кабінет ДПС — єдиний журнал відправок електронних документів у ДПС
 * (розділ «Звітність»). Тут же отримуються квитанції №2: «авторизація» ключем
 * КЕП (читається у браузері на сесію) → кнопка «оновити статус» шифрує id
 * пакета ключем і тягне рішення органу. Текст квитанції розшифровується тут.
 * Документ показує лише статуси; всі дії з квитанціями — у цьому журналі.
 */

const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' }) : '';

const STATUS: Record<string, { text: string; color: string }> = {
  DRAFT: { text: 'чернетка', color: '#64748b' },
  SIGNED: { text: 'підписано', color: '#7c3aed' },
  SENT: { text: 'відправлено', color: '#1d4ed8' },
  DELIVERED: { text: 'доставлено, обробляється', color: '#0369a1' },
  ACCEPTED: { text: 'прийнято', color: '#16a34a' },
  REJECTED: { text: 'не прийнято', color: '#dc2626' },
  ERROR: { text: 'помилка', color: '#dc2626' },
};

// Людська назва форми звіту за кодом (reg_doc). Квитанції — «Квитанція».
const REG_DOC_NAMES: Record<string, string> = {
  J0500110: 'Податковий розрахунок (ПДФО/ВЗ/ЄСВ)',
  J0200120: 'Декларація з ПДВ',
  J0100120: 'Декларація з податку на прибуток',
  J3000512: 'Об’єднана звітність ЄСВ',
  BOTB0501: 'Податкові зобов’язання майбутніх періодів',
  BOTB0101: 'Податковий борг',
  J1400306: 'Довідка про відсутність заборгованості з платежів',
  J1400207: 'Витяг з ІКС ДПС про стан розрахунків платника (з пенею)',
  J1400208: 'Витяг про стан розрахунків з бюджетом',
  J1420401: 'Витяг про стан розрахунків з бюджетом',
  J1430001: 'Відмова у погодженні Довідки-розрахунку',
  D0300201: 'Повідомлення Світового банку',
  MSG: 'Інформаційне повідомлення',
};
// Назва форми: спершу docName з API (людська назва від ДПС), інакше словник,
// інакше «Форма <код>». Квитанції — окремо.
function regDocName(r: { doc?: string | null; docName?: string | null }): string {
  const doc = r.doc ?? null;
  if (doc && /^[JF]1499/.test(doc)) {
    return doc.startsWith('J14992') || doc.startsWith('F14992')
      ? 'Квитанція №2' : 'Квитанція №1';
  }
  if (r.docName && r.docName.trim()) return r.docName.trim();
  if (doc && REG_DOC_NAMES[doc]) return REG_DOC_NAMES[doc];
  return doc ? `Форма ${doc}` : 'Звіт';
}
// Дата/час подання звіту — поле dget з API.
function regDate(r: { dget?: string | null }): string {
  return r.dget || '—';
}

// Назва вхідного документа (post/incoming): docName → text(очищений) → словник → код.
function incLabel(r: { cdoc?: string | null; docName?: string | null;
  text?: string | null }): string {
  if (r.docName && r.docName.trim()) return r.docName.trim();
  if (r.text && r.text.trim()) {
    const plain = r.text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (plain) return plain.length <= 120 ? plain : plain.slice(0, 117) + '…';
  }
  const c = r.cdoc ?? null;
  if (c && REG_DOC_NAMES[c]) return REG_DOC_NAMES[c];
  return c ? `Документ ${c}` : 'Документ';
}

const MONTHS_UA = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень',
  'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];
const QUARTERS_UA = ['', 'I', 'II', 'III', 'IV'];

/** Період подання, як його показує веб-кабінет ДПС: «Липень 2026» /
 * «II кв. 2026» (period_type: 1=місяць, 2=квартал). */
function periodLabel(r: { period_year?: number | null;
  period_month?: number | null; period_type?: number | null }): string {
  if (!r.period_year) return '—';
  if (r.period_type === 2) {
    const q = Math.ceil((r.period_month ?? 0) / 3);
    return q ? `${QUARTERS_UA[q]} кв. ${r.period_year}` : String(r.period_year);
  }
  const m = r.period_month ?? 0;
  return m ? `${MONTHS_UA[m]} ${r.period_year}` : String(r.period_year);
}

// Вхідні, що є ПОВІДОМЛЕННЯМ без окремого документа-файла (BOTB*, MSG) —
// показуємо текст повідомлення, а не тягнемо PDF (його немає).
function isMessageOnly(cdoc?: string | null): boolean {
  const c = (cdoc ?? '').toUpperCase();
  return c.startsWith('BOTB') || c === 'MSG';
}

// source_type → маршрут документа-джерела (для переходу «відкрити документ»)
const SOURCE_ROUTE: Record<string, (id: string) => string> = {
  'doc.hire_notifications': (id) => `/hire-notifications/${id}`,
  'doc.dps_requests': (id) => `/dps-cabinet/requests/${id}`,
  'doc.tax_reports': (id) => `/tax-reports-j05/${id}`,
};

// source_type → як отримати XML документа для підписання (blob + filename).
const SOURCE_XML: Record<string, (id: string) => Promise<any>> = {
  'doc.hire_notifications': (id) => hireNotifications.xml(id),
  'doc.dps_requests': (id) => dpsRequests.xml(id),
};

export default function DpsCabinetPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [orgId, setOrgId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Пошук по Журналу: код форми (J0500110) і назва документа.
  const [formQ, setFormQ] = useState('');
  const [titleQ, setTitleQ] = useState('');
  const [keys, setKeys] = useState<StoredKey[]>(() => [...listKeys()]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Квитанція у попапі — друкований бланк (форма J1499202).
  const [receiptView, setReceiptView] =
    useState<{ fields: ReceiptFields; org: string } | null>(null);
  const [tab, setTab] = useState<'journal' | 'incoming' | 'reports'>('journal');
  const [privBusy, setPrivBusy] = useState(false);
  // Перегляд документа кабінету у ПОПАПІ (не нове вікно — браузери блокують):
  // або PDF (blob у iframe), або HTML-текст (стан розрахунків тощо).
  const [cabView, setCabView] = useState<
    { title: string; pdfUrl?: string; html?: string; keepUrl?: boolean } | null>(null);
  // «Звітність» (reg_doc): період + список поданих звітів (тягнеться з ключем).
  const now = new Date();
  const [repYear, setRepYear] = useState(now.getFullYear());
  const [repMonth, setRepMonth] = useState(now.getMonth() + 1);
  const [reports, setReports] = useState<DpsRegDoc[] | null>(null);
  // Файли розкритого звіту (осн.+додатки+квитанції) у попапі.
  const [repFiles, setRepFiles] = useState<
    { title: string; orgId: string; year: number; items: DpsRegDoc[] } | null>(null);
  // Кеш «Звітності» у пам'яті вкладки — щоб не смикати ДПС повторно
  // (менше запитів = безпечніше по лімітах; F5 скидає). Ключі: список за
  // «org|рік|місяць»; файли за «org|рік|codRegdoc»; PDF за «org|рік|codRegdoc».
  const repListCache = useRef<Map<string, DpsRegDoc[]>>(new Map());
  const repFilesCache = useRef<Map<string, DpsRegDoc[]>>(new Map());
  const repPdfCache = useRef<Map<string, string>>(new Map());  // → blob url
  // «Вхідні» (post/incoming) — як «Звітність»: тягнемо з ДПС на вимогу, НЕ
  // зберігаємо; кеш у пам'яті вкладки. Квитанції (J1499xxx) НЕ показуємо.
  // Пагінація як у ДПС: тягнемо по одній сторінці (Spring page/last).
  const [incoming, setIncoming] = useState<DpsPrivIncoming[] | null>(null);
  const [incPage, setIncPage] = useState(0);
  const [incLast, setIncLast] = useState(true);
  const incListCache = useRef<Map<string, DpsPrivIncoming[]>>(new Map());  // org|page → сторінка
  const incLastCache = useRef<Map<string, boolean>>(new Map());  // org|page → last?
  const incPdfCache = useRef<Map<string, string>>(new Map());  // org|year|id → blob
  const keyReady = keys.length > 0;

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const listQ = useQuery({
    queryKey: ['dps-submissions', orgId, dateFrom, dateTo],
    queryFn: () => dpsSubmissions.list({
      organization_id: orgId || undefined, limit: 500,
      date_from: dateFrom || undefined, date_to: dateTo || undefined,
    }),
  });


  const orgName = useMemo(
    () => new Map((orgList.data?.items ?? []).map((o) => [o.id, o.name])),
    [orgList.data]);
  // orgId → код особи (ЄДРПОУ юрособи або РНОКПП/ІПН ФОП) для звірки з ключем.
  const orgCode = useMemo(
    () => new Map((orgList.data?.items ?? []).map(
      (o) => [o.id, (o.edrpou || o.inn || '').trim()])),
    [orgList.data]);

  const rows = useMemo<DpsSubmission[]>(() => {
    const fq = formQ.trim().toLowerCase();
    const tq = titleQ.trim().toLowerCase();
    return (listQ.data?.items ?? []).filter((r: DpsSubmission) => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (fq && !(r.form_code ?? '').toLowerCase().includes(fq)) return false;
      if (tq && !(r.form_title ?? '').toLowerCase().includes(tq)) return false;
      return true;
    });
  }, [listQ.data, statusFilter, formQ, titleQ]);

  // Порівняння кодів «як ДПС»: без провідних нулів (ЄДРПОУ 8 vs 10 цифр).
  const sameCode = (a: string, b: string) =>
    a.replace(/^0+/, '') === b.replace(/^0+/, '') && a.replace(/^0+/, '') !== '';

  /**
   * Обрати і активувати ключ, яким підписувати документ цієї юрособи.
   *
   * - 0 ключів → помилка «зчитайте ключ»;
   * - шукаємо ключ, чий код (ЄДРПОУ/РНОКПП) збігається з кодом юрособи
   *   документа → активуємо його (звірка ДО підпису, щоб не було відмови ДПС);
   * - код юрособи невідомий (не завантажився список) → беремо активний/перший;
   * - жоден ключ не підходить → помилка з переліком, ЧИМ можна підписати.
   *
   * Повертає власника активованого ключа (для signer_edrpou у запиті на сервер).
   */
  async function pickKeyForOrg(organizationId: string): Promise<KeyOwnerInfo> {
    const list = listKeys();
    if (!list.length) throw new Error('Спершу зчитайте ключ КЕП (угорі).');
    const wantCode = (orgCode.get(organizationId) ?? '').trim();
    const first = list[0];
    if (!wantCode && first) {
      // Код юрособи невідомий — не блокуємо (сервер додатково звірить).
      return activateKey(first.id);
    }
    const match = list.find((k) => sameCode(keyCode(k.owner), wantCode));
    if (match) return activateKey(match.id);
    const orgLabel = orgName.get(organizationId) ?? 'цієї юрособи';
    const have = list.map((k) => keyCode(k.owner)).filter(Boolean).join(', ');
    throw new Error(
      `Немає зчитаного ключа для «${orgLabel}» (код ${wantCode}). `
      + `Зчитано ключі: ${have || '—'}. Додайте ключ саме цієї юрособи `
      + `(кнопка «+ додати ще ключ КЕП»), інакше ДПС відхилить документ.`);
  }

  // «Вхідні»: підписуємо ЄДРПОУ ключем → тягнемо список вхідних з ДПС (усі
  // сторінки), КВИТАНЦІЇ (J1499xxx) відкидаємо. Кеш у пам'яті — повторне
  // відкриття без запиту до ДПС (F5 скидає). Як «Звітність».
  // Одна сторінка вхідних (як пагінація ДПС): не тягнемо все одразу.
  async function loadIncoming(targetOrgId: string, page = 0, force = false) {
    setError(null);
    const cacheKey = `${targetOrgId}|${page}`;
    if (!force) {
      const cached = incListCache.current.get(cacheKey);
      if (cached) {
        setIncoming(cached);
        setIncPage(page);
        setIncLast(incLastCache.current.get(cacheKey) ?? true);
        return;
      }
    }
    setPrivBusy(true); setIncoming(null);
    try {
      const owner = await pickKeyForOrg(targetOrgId);
      const code = owner.subjEDRPOUCode || owner.subjDRFOCode || '';
      const authHeader = signApiAuthHeader(code);
      const data = await dpsPrivApi.incoming({
        auth_header: authHeader, organization_id: targetOrgId,
        signer_edrpou: code, page,
      });
      const chunk = data.content ?? [];
      // КВИТАНЦІЇ (J1499xxx/F1499xxx) — не показуємо у «Вхідних».
      const docs = chunk.filter((r) => !(r.cdoc && /^[JF]1499/.test(r.cdoc)));
      // Кінець: ДПС каже last, або totalPages вичерпано, або сторінка порожня.
      const isLast = data.last === true
        || (typeof data.totalPages === 'number' && page + 1 >= data.totalPages)
        || chunk.length === 0;
      incListCache.current.set(cacheKey, docs);
      incLastCache.current.set(cacheKey, isLast);
      setIncoming(docs);
      setIncPage(page);
      setIncLast(isLast);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setPrivBusy(false);
    }
  }

  // Відкрити PDF вхідного документа (тягнемо з ДПС на вимогу, кеш blob).
  async function openIncomingPdf(row: DpsPrivIncoming) {
    if (!orgId) return;
    setError(null);
    // Повідомлення (BOTB*, MSG) — окремого документа немає: показуємо текст.
    if (isMessageOnly(row.cdoc)) {
      const body = (row.text && row.text.trim())
        ? row.text
        : '<p style="color:#64748b">Текст повідомлення відсутній.</p>';
      setCabView({
        title: incLabel(row),
        html: `<div style="padding:1rem;font-size:0.9rem;line-height:1.5">${body}</div>`,
      });
      return;
    }
    const year = row.periodYear ?? now.getFullYear();
    const cacheKey = `${orgId}|${year}|${row.id}`;
    const cachedUrl = incPdfCache.current.get(cacheKey);
    if (cachedUrl) {
      setCabView({ title: incLabel(row), pdfUrl: cachedUrl, keepUrl: true });
      return;
    }
    setPrivBusy(true);
    try {
      const owner = await pickKeyForOrg(orgId);
      const code = owner.subjEDRPOUCode || owner.subjDRFOCode || '';
      const authHeader = signApiAuthHeader(code);
      const resp = await dpsPrivApi.file({
        auth_header: authHeader, organization_id: orgId,
        signer_edrpou: code, year, doc_id: String(row.id), fmt: 'pdf',
      });
      const url = URL.createObjectURL(
        new Blob([resp.data], { type: 'application/pdf' }));
      incPdfCache.current.set(cacheKey, url);
      setCabView({ title: incLabel(row), pdfUrl: url, keepUrl: true });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setPrivBusy(false);
    }
  }

  // «Звітність»: завантажити список поданих звітів за період (підпис ключем).
  async function loadReports(targetOrgId: string) {
    setError(null);
    const cacheKey = `${targetOrgId}|${repYear}|${repMonth}`;
    const cached = repListCache.current.get(cacheKey);
    if (cached) { setReports(cached); return; }  // з кешу — без запиту до ДПС
    setPrivBusy(true); setReports(null);
    try {
      const owner = await pickKeyForOrg(targetOrgId);
      const code = owner.subjEDRPOUCode || owner.subjDRFOCode || '';
      const authHeader = signApiAuthHeader(code);
      const data = await dpsRegDoc.list({
        auth_header: authHeader, organization_id: targetOrgId,
        signer_edrpou: code, period_year: repYear, period_month: repMonth,
      });
      const list = data.content ?? [];
      repListCache.current.set(cacheKey, list);
      setReports(list);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setPrivBusy(false);
    }
  }

  // Розкрити файли одного звіту (осн. + додатки + квитанції).
  async function openReportFiles(row: DpsRegDoc, targetOrgId: string) {
    setError(null);
    const year = row.periodYear ?? repYear;
    const cacheKey = `${targetOrgId}|${year}|${row.codRegdoc}`;
    const title = `${regDocName(row)} — ${row.periodMonth ?? ''}/${year}`;
    const cached = repFilesCache.current.get(cacheKey);
    if (cached) {  // з кешу — без запиту до ДПС
      setRepFiles({ title, orgId: targetOrgId, year, items: cached });
      return;
    }
    setPrivBusy(true);
    try {
      const owner = await pickKeyForOrg(targetOrgId);
      const code = owner.subjEDRPOUCode || owner.subjDRFOCode || '';
      const authHeader = signApiAuthHeader(code);
      const items = (await dpsRegDoc.files({
        auth_header: authHeader, organization_id: targetOrgId,
        signer_edrpou: code, year, cod_regdoc: String(row.codRegdoc),
      })) ?? [];
      repFilesCache.current.set(cacheKey, items);
      setRepFiles({ title, orgId: targetOrgId, year, items });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setPrivBusy(false);
    }
  }

  // Скачати конкретний файл звіту (PDF) з reg_doc.
  async function downloadRegDoc(row: DpsRegDoc) {
    if (!repFiles) return;
    setError(null);
    const year = row.periodYear ?? repFiles.year;
    const cacheKey = `${repFiles.orgId}|${year}|${row.codRegdoc}`;
    const cachedUrl = repPdfCache.current.get(cacheKey);
    if (cachedUrl) {  // PDF уже качали — з кешу, без запиту до ДПС
      setCabView({ title: regDocName(row), pdfUrl: cachedUrl, keepUrl: true });
      return;
    }
    setPrivBusy(true);
    try {
      const owner = await pickKeyForOrg(repFiles.orgId);
      const code = owner.subjEDRPOUCode || owner.subjDRFOCode || '';
      const authHeader = signApiAuthHeader(code);
      const resp = await dpsRegDoc.file({
        auth_header: authHeader, organization_id: repFiles.orgId,
        signer_edrpou: code, year, cod_regdoc: String(row.codRegdoc), fmt: 'pdf',
      });
      const url = URL.createObjectURL(
        new Blob([resp.data], { type: 'application/pdf' }));
      repPdfCache.current.set(cacheKey, url);
      setCabView({ title: regDocName(row), pdfUrl: url, keepUrl: true });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setPrivBusy(false);
    }
  }

  // «оновити статус»: шифруємо id пакета сесійним ключем → тягнемо кв.№2.
  // ДІАГНОСТИКА: точний формат encryptedId ДПС не документує — перебираємо
  // варіанти, доки відповідь не перестане бути NOT_KVT (лог на backend
  // пише повну відповідь ДПС по кожному варіанту).
  async function refresh(sub: DpsSubmission) {
    if (sub.dps_report_id == null) { setError('Пакет без ідентифікатора ДПС.'); return; }
    setBusyId(sub.id); setError(null);
    try {
      // Обрати й активувати ключ саме цієї юрособи (розшифрувати id пакета
      // можна лише ключем, на який ДПС шифрувала квитанцію).
      const owner = await pickKeyForOrg(sub.organization_id);
      const signerEdrpou = owner.subjEDRPOUCode ?? owner.subjDRFOCode;
      const variants = encryptReportIdVariants(sub.dps_report_id, sub.file_name);
      let fresh: DpsSubmission | null = null;
      let worked: string | null = null;
      for (const v of variants) {
        const encId = await v.build();
        fresh = await dpsSubmissions.refresh(sub.id, encId, signerEdrpou);
        if (fresh.kvt2_base64 || (fresh.dps_status_code
            && fresh.dps_status_code !== 'NOT_KVT')) {
          worked = v.label;
          break;
        }
      }
      if (fresh && worked) {
        console.info(`kvt_by_id: спрацював формат «${worked}»`);
        await cacheReceipts(fresh);
      } else {
        setError('ДПС відповідає NOT_KVT на всі формати запиту — імовірно, '
          + 'квитанція №2 ще не сформована на стороні ДПС. Деталі — у логах.');
      }
      qc.invalidateQueries({ queryKey: ['dps-submissions'] });
      qc.invalidateQueries({ queryKey: ['dps-incoming'] });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  // «підписати»: XML документа → повний транспортний контейнер (ключ у
  // браузері) → зберегти на сервері (status=SIGNED). Відправка — окремо.
  // Для ПАКЕТА (doc.tax_reports) — підписати КОЖЕН файл (осн+додатки) і
  // зберегти JSON-масив [{fname, container}]; відправка — одним /report.
  async function signSub(sub: DpsSubmission) {
    setBusyId(sub.id); setError(null);
    const email = orgList.data?.items.find(
      (o) => o.id === sub.organization_id)?.email ?? '';
    try {
      // Обрати й активувати ключ саме цієї юрособи + звірити код (ЄДРПОУ/
      // РНОКПП) ДО підпису — інакше ДПС відхилить «підписано не тією особою».
      const owner = await pickKeyForOrg(sub.organization_id);
      const issuerCN = owner.issuerCN ?? '';
      const signerEdrpou = owner.subjEDRPOUCode ?? owner.subjDRFOCode;
      if (sub.source_type === 'doc.tax_reports') {
        const { files } = await taxReportJ05.files(sub.source_id);
        const pkg: { fname: string; container: string }[] = [];
        for (const f of files) {
          const bytes = Uint8Array.from(atob(f.xml_base64), (c) => c.charCodeAt(0));
          const container = await buildSignedContainer(bytes, f.fname, issuerCN, email);
          pkg.push({ fname: f.fname, container: bytesToBase64(container) });
        }
        await dpsSubmissions.sign(
          sub.id, JSON.stringify(pkg), files[0]?.fname ?? 'package', signerEdrpou);
        qc.invalidateQueries({ queryKey: ['dps-submissions'] });
        qc.invalidateQueries({ queryKey: ['dps-incoming'] });
        return;
      }
      const getXml = SOURCE_XML[sub.source_type];
      if (!getXml) { setError(`Невідоме джерело ${sub.source_type}`); return; }
      const resp = await getXml(sub.source_id);
      const cd: string = resp.headers['content-disposition'] ?? '';
      const m = cd.match(/filename="([^"]+)"/);
      // Ім'я файлу — ЯК ЗГЕНЕРУВАВ backend (…2657.xml, нижнє розширення —
      // саме так ДПС пише імена у своїх квитанціях; НЕ upper-case).
      const filename = m?.[1] ?? `${sub.form_code}.xml`;
      const bytes = new Uint8Array(await (resp.data as Blob).arrayBuffer());
      const container = await buildSignedContainer(
        bytes, filename, issuerCN, email);
      await dpsSubmissions.sign(
        sub.id, bytesToBase64(container), filename, signerEdrpou);
      qc.invalidateQueries({ queryKey: ['dps-submissions'] });
      qc.invalidateQueries({ queryKey: ['dps-incoming'] });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  // Видалити чернетку (лише DRAFT/ERROR): прибирає запис журналу + сам
  // податковий розрахунок (якщо він ще не поданий). Успішні — не чіпаємо.
  async function removeDraft(sub: DpsSubmission) {
    if (sub.status !== 'DRAFT' && sub.status !== 'ERROR') return;
    if (!window.confirm(
      'Видалити чернетку та відповідний розрахунок? Дію не можна скасувати.')) {
      return;
    }
    setBusyId(sub.id); setError(null);
    try {
      await dpsSubmissions.remove(sub.id);
      qc.invalidateQueries({ queryKey: ['dps-submissions'] });
      qc.invalidateQueries({ queryKey: ['dps-incoming'] });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  // «відправити»: шле збережений підписаний контейнер (ключ не потрібен).
  async function sendSub(sub: DpsSubmission) {
    setBusyId(sub.id); setError(null);
    try {
      const res = await dpsSubmissions.send(sub.id);
      if (res.dps_status && res.dps_status !== 'OK') {
        setError(`ДПС: ${res.dps_status}${res.message ? ` — ${res.message}` : ''}`);
      }
      qc.invalidateQueries({ queryKey: ['dps-submissions'] });
      qc.invalidateQueries({ queryKey: ['dps-incoming'] });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  // Розшифрований XML квитанції №num: спершу кеш (receiptN_xml, без ключа),
  // інакше розшифрувати ключем і закешувати. Повертає XML або null.
  async function receiptXml(sub: DpsSubmission, num: 1 | 2): Promise<string | null> {
    const cached = num === 1 ? sub.receipt1_xml : sub.receipt2_xml;
    if (cached) return cached;
    const kvt = num === 1 ? sub.kvt1_base64 : sub.kvt2_base64;
    if (!kvt) return null;
    await pickKeyForOrg(sub.organization_id);  // ключ юрособи у слот
    const xml = developReceiptToXml(kvt);
    // Кеш на сервері (XML + читабельний текст) — далі бланк без ключа.
    const f = parseReceiptFieldsFromXml(xml);
    await dpsSubmissions.saveReceiptText(
      sub.id, f.text || f.result, f.accepted, xml, num)
      .then(() => qc.invalidateQueries({ queryKey: ['dps-submissions'] }))
      .catch(() => {});
    return xml;
  }

  // Відкрити квитанцію №num друкованим бланком (форма J1499202).
  async function openReceiptNum(sub: DpsSubmission, num: 1 | 2) {
    setError(null);
    setBusyId(sub.id);
    try {
      const xml = await receiptXml(sub, num);
      if (!xml) { setError(`Квитанції №${num} ще немає.`); return; }
      setReceiptView({
        fields: parseReceiptFieldsFromXml(xml),
        org: orgName.get(sub.organization_id) ?? '',
      });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  // Розшифрувати наявні квитанції тихо (для кешу після refresh) — без попапа.
  async function cacheReceipts(sub: DpsSubmission) {
    try {
      if (sub.kvt1_base64 && !sub.receipt1_xml) await receiptXml(sub, 1);
      if (sub.kvt2_base64 && !sub.receipt2_xml) await receiptXml(sub, 2);
    } catch { /* кеш не критичний — квитанцію все одно відкриють */ }
  }

  // Друк/збереження бланка квитанції у PDF — системний діалог друку браузера
  // (лишається лише бланк завдяки .dps-printing у styles.css).
  function printReceipt() {
    document.body.classList.add('dps-printing');
    window.print();
    setTimeout(() => document.body.classList.remove('dps-printing'), 500);
  }

  const cols = useMemo<ColumnDef<DpsSubmission>[]>(() => [
    {
      key: 'created_at', header: 'Дата', width: 140,
      sortValue: (r) => r.created_at, cell: (r) => fmtDate(r.created_at),
    },
    {
      key: 'title', header: 'Назва', width: 280,
      sortValue: (r) => r.form_title ?? r.form_code,
      cell: (r) => {
        const route = SOURCE_ROUTE[r.source_type]?.(r.source_id);
        const label = r.form_title ?? r.form_code;
        return route
          ? <a style={{ cursor: 'pointer' }} onClick={() => navigate(route)}
              title={`${r.form_code} · ${r.file_name}`}>{label}</a>
          : <span title={r.form_code}>{label}</span>;
      },
    },
    {
      key: 'form_code', header: 'Форма', width: 100,
      sortValue: (r) => r.form_code,
      cell: (r) => <span title={r.file_name}>{r.form_code}</span>,
    },
    {
      key: 'period', header: 'Період', width: 120,
      sortValue: (r) => (r.period_year ?? 0) * 100 + (r.period_month ?? 0),
      cell: (r) => periodLabel(r),
    },
    {
      key: 'org', header: 'Юрособа', width: 170,
      sortValue: (r) => (orgName.get(r.organization_id) ?? '').toLowerCase(),
      cell: (r) => orgName.get(r.organization_id) ?? '—',
    },
    {
      key: 'status', header: 'Статус', width: 150,
      sortValue: (r) => r.status,
      cell: (r) => {
        const s = STATUS[r.status] ?? { text: r.status, color: '#475569' };
        return (
          <span style={{ color: s.color, fontWeight: 600 }}
            title={r.error_message ?? r.dps_status_code ?? ''}>
            {s.text}
          </span>
        );
      },
    },
    {
      key: 'act', header: 'Дія', width: 130, align: 'center',
      sortValue: () => 0,
      cell: (r) => {
        const isDraft = r.status === 'DRAFT' || r.status === 'ERROR';
        const isSigned = r.status === 'SIGNED';
        const has2 = !!(r.kvt2_base64 || r.receipt2_xml);
        // «Оновити» (запит кв.№2) — доступно, поки подача не фіналізована.
        const needsKvt2 = (r.status === 'SENT' || r.status === 'DELIVERED') && !has2;
        const busy = busyId === r.id;
        return (
          <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
            {(isDraft || isSigned) && (
              <IconBtn label={isSigned ? 'Перепідписати ключем КЕП'
                  : 'Підписати ключем КЕП (відправка — окремо)'}
                disabled={busy || !keyReady} busy={busy}
                hint={!keyReady ? 'Спершу зчитайте ключ КЕП (угорі)' : undefined}
                onClick={() => signSub(r)}>
                <IconSign />
              </IconBtn>
            )}
            {isSigned && (
              <IconBtn label="Відправити підписаний документ у ДПС"
                color="#2563eb" disabled={busy} busy={busy}
                onClick={() => sendSub(r)}>
                <IconSend />
              </IconBtn>
            )}
            {needsKvt2 && (
              <IconBtn label="Оновити статус — запитати квитанцію №2 у ДПС"
                disabled={busy || !keyReady} busy={busy}
                hint={!keyReady ? 'Спершу зчитайте ключ КЕП (угорі)' : undefined}
                onClick={() => refresh(r)}>
                <IconRefresh />
              </IconBtn>
            )}
            {isDraft && (
              <IconBtn label={r.source_type === 'doc.tax_reports'
                  ? 'Видалити чернетку та розрахунок' : 'Видалити чернетку'}
                color="#dc2626" disabled={busy} busy={busy}
                onClick={() => removeDraft(r)}>
                <IconTrash />
              </IconBtn>
            )}
          </span>
        );
      },
    },
    {
      key: 'kvt', header: 'Квитанції', width: 120, align: 'left',
      sortValue: (r) => (r.kvt2_base64 ? 2 : r.kvt1_base64 ? 1 : 0),
      cell: (r) => {
        const has1 = !!(r.kvt1_base64 || r.receipt1_xml);
        const has2 = !!(r.kvt2_base64 || r.receipt2_xml);
        // Кеш XML квитанції заповнюється на сервері при отриманні (тіло
        // підписане відкрито) → бланк відкривається БЕЗ ключа. Ключ потрібен
        // лише для рідкісної зашифрованої квитанції (немає receiptN_xml).
        const open1 = !!r.receipt1_xml || (has1 && keyReady);
        const open2 = !!r.receipt2_xml || (has2 && keyReady);
        // «Оновити» (запит кв.№2) переїхало у колонку «Дія».
        return (
          <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center',
            flexWrap: 'wrap' }}>
            <Kvt n={1} active={has1} clickable={open1}
              busy={busyId === r.id} onClick={() => openReceiptNum(r, 1)} />
            <Kvt n={2} active={has2} clickable={open2}
              busy={busyId === r.id} onClick={() => openReceiptNum(r, 2)} />
          </span>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [navigate, orgName, busyId, keyReady, orgList.data]);



  // Селект юрособи — спільний для всіх вкладок (у кожної свій рядок фільтрів).
  const orgSelect = (
    <select value={orgId}
      onChange={(e) => {
        setOrgId(e.target.value);
        // Зміна юрособи → скинути показані вхідні/звіти (інша юрособа = інші дані).
        setIncoming(null); setIncPage(0); setIncLast(true);
        setReports(null); setRepFiles(null);
      }}
      style={{ width: 220, flex: '0 0 auto' }}>
      <option value="">Усі юрособи</option>
      {orgList.data?.items.map((o) => (
        <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
      ))}
    </select>
  );

  return (
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>Кабінет ДПС</h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/dps-cabinet/new')}>+ створити</button>
      </div>

      {/* 1) Авторизація ключами КЕП — бейджі зчитаних ключів (кілька юросіб);
          потрібний обирається автоматично за юрособою документа. */}
      <KeyAuthPanel onKeysChange={setKeys} />

      <div className="seg-tabs" style={{ marginBottom: '0.6rem' }}>
        <button className={tab === 'journal' ? 'active' : ''}
          onClick={() => setTab('journal')}>Журнал</button>
        <button className={tab === 'incoming' ? 'active' : ''}
          onClick={() => setTab('incoming')}>
          Вхідні{incoming ? ` (${incoming.length})` : ''}
        </button>
        <button className={tab === 'reports' ? 'active' : ''}
          onClick={() => setTab('reports')}>Звітність</button>
      </div>

      {/* 2) Фільтри — свої для кожної вкладки. */}
      {tab === 'journal' && (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
          marginBottom: '0.5rem', flexWrap: 'wrap' }}>
          {orgSelect}
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            style={{ width: 150, flex: '0 0 auto' }}>
            <option value="">Усі статуси</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.text}</option>)}
          </select>
          <input value={titleQ} onChange={(e) => setTitleQ(e.target.value)}
            placeholder="Назва…" title="Пошук серед назв документів"
            style={{ width: 170, flex: '0 0 auto' }} />
          <input value={formQ} onChange={(e) => setFormQ(e.target.value)}
            placeholder="Форма…" title="Пошук за кодом форми (напр. J0500110)"
            style={{ width: 120, flex: '0 0 auto' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>з</span>
            <div style={{ width: 140 }}>
              <DatePopover value={dateFrom} onChange={setDateFrom} />
            </div>
            <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>по</span>
            <div style={{ width: 140 }}>
              <DatePopover value={dateTo} onChange={setDateTo} />
            </div>
            {(dateFrom || dateTo) && (
              <button style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
                onClick={() => { setDateFrom(''); setDateTo(''); }}>×</button>
            )}
          </div>
        </div>
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
          Збережений звітний документ з'являється тут чернеткою автоматично.
          Зчитайте ключ КЕП → «підписати» → «відправити». Квитанція №2 (рішення
          органу) приходить із затримкою — далі підтягується сама.
        </p>
      </>
      )}
      {tab === 'incoming' && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
        marginTop: 0, marginBottom: '0.6rem', flexWrap: 'wrap' }}>
        {orgSelect}
        <button className="primary"
          style={{ padding: '0.25rem 0.7rem', fontSize: '0.82rem' }}
          disabled={privBusy || !keyReady || !orgId}
          title={!keyReady ? 'Спершу зчитайте ключ КЕП (угорі)'
            : !orgId ? 'Оберіть юрособу'
              : 'Підписати ЄДРПОУ ключем і показати вхідні з кабінету ДПС'}
          onClick={() => orgId && loadIncoming(orgId, 0)}>
          {privBusy ? 'завантаження…' : 'показати'}
        </button>
        {incoming !== null && (
          <button style={{ padding: '0.25rem 0.6rem', fontSize: '0.82rem' }}
            disabled={privBusy || !keyReady || !orgId}
            title="Оновити з ДПС (ігнорує кеш, з першої сторінки)"
            onClick={() => {
              if (!orgId) return;
              // Скинути кеш усіх сторінок цієї юрособи → тягнути заново.
              for (const k of [...incListCache.current.keys()])
                if (k.startsWith(`${orgId}|`)) incListCache.current.delete(k);
              incLastCache.current.clear();
              incPdfCache.current.clear();  // биті PDF з кабінету — перетягнути
              loadIncoming(orgId, 0, true);
            }}>
            оновити
          </button>
        )}
        <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
          {!orgId ? 'оберіть юрособу'
            : 'вхідні з кабінету ДПС: витяги, довідки, ППР, повідомлення'}
        </span>
      </div>
      )}

      {error && (
        <div style={{ background: '#fee', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>{error}</div>
      )}

      {tab === 'journal' && (
      <DataTable<DpsSubmission>
        rows={rows}
        columns={cols}
        rowKey={(r) => r.id}
        loading={listQ.isLoading}
        emptyText="Ще нічого не відправляли у ДПС."
        initialSortKey="created_at"
        initialSortDir="desc"
      />
      )}

      {tab === 'incoming' && incoming !== null && (
        <div>
          {incoming.length === 0 && incPage === 0 && incLast ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              Вхідних документів немає.
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                  <th style={{ padding: '0.35rem', width: 150 }}>Дата</th>
                  <th style={{ padding: '0.35rem', width: 110 }}>Форма</th>
                  <th style={{ padding: '0.35rem' }}>Назва</th>
                  <th style={{ padding: '0.35rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {incoming.length === 0 ? (
                  <tr><td colSpan={4} style={{ padding: '0.35rem', color: 'var(--muted)' }}>
                    На цій сторінці лише квитанції (їх не показуємо). Гортайте далі.
                  </td></tr>
                ) : incoming.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '0.35rem' }}>{r.dateIn ?? '—'}</td>
                    <td style={{ padding: '0.35rem' }}>{r.cdoc ?? '—'}</td>
                    <td style={{ padding: '0.35rem' }}>
                      {r.isRead === 0 && (
                        <span style={{ color: '#dc2626', fontWeight: 700, marginRight: 4 }}
                          title="Новий (непрочитаний у кабінеті)">●</span>
                      )}
                      {incLabel(r)}
                    </td>
                    <td style={{ padding: '0.35rem', textAlign: 'right' }}>
                      <button style={{ padding: '0.15rem 0.55rem', fontSize: '0.78rem' }}
                        disabled={privBusy}
                        onClick={() => openIncomingPdf(r)}>
                        {privBusy ? '…'
                          : isMessageOnly(r.cdoc) ? 'повідомлення' : 'переглянути'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {/* Пагінація як у ДПС: сторінка за сторінкою (не тягнемо все). */}
          {!(incoming.length === 0 && incPage === 0 && incLast) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem',
              marginTop: '0.6rem', fontSize: '0.82rem' }}>
              <button style={{ padding: '0.2rem 0.6rem' }}
                disabled={privBusy || incPage === 0}
                onClick={() => orgId && loadIncoming(orgId, incPage - 1)}>
                ← назад
              </button>
              <span style={{ color: 'var(--muted)' }}>сторінка {incPage + 1}</span>
              <button style={{ padding: '0.2rem 0.6rem' }}
                disabled={privBusy || incLast}
                onClick={() => orgId && loadIncoming(orgId, incPage + 1)}>
                далі →
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'reports' && (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
          marginBottom: '0.6rem', flexWrap: 'wrap' }}>
          {orgSelect}
          <label style={{ fontSize: '0.82rem' }}>Період:</label>
          <select value={repMonth} onChange={(e) => setRepMonth(Number(e.target.value))}
            style={{ width: 130 }}>
            {['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
              'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень']
              .map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={repYear} onChange={(e) => setRepYear(Number(e.target.value))}
            style={{ width: 90 }}>
            {[0, 1, 2, 3, 4].map((d) => {
              const y = now.getFullYear() - d;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
          <button className="primary"
            style={{ padding: '0.25rem 0.7rem', fontSize: '0.82rem' }}
            disabled={privBusy || !keyReady || !orgId}
            title={!keyReady ? 'Спершу зчитайте ключ КЕП (угорі)'
              : !orgId ? 'Оберіть юрособу' : 'Показати звітність за період'}
            onClick={() => orgId && loadReports(orgId)}>
            {privBusy ? 'завантаження…' : 'показати'}
          </button>
          {reports !== null && (
            <button style={{ padding: '0.25rem 0.6rem', fontSize: '0.82rem' }}
              disabled={privBusy || !keyReady || !orgId}
              title="Перетягнути з ДПС наново (скидає кеш списку, файлів і PDF —
якщо документ завантажився з битою кодировкою)"
              onClick={() => {
                if (!orgId) return;
                // Скинути ВЕСЬ кеш цієї юрособи: список періоду, файли, PDF-blob.
                for (const k of [...repListCache.current.keys()])
                  if (k.startsWith(`${orgId}|`)) repListCache.current.delete(k);
                for (const k of [...repFilesCache.current.keys()])
                  if (k.startsWith(`${orgId}|`)) repFilesCache.current.delete(k);
                for (const k of [...repPdfCache.current.keys()])
                  if (k.startsWith(`${orgId}|`)) repPdfCache.current.delete(k);
                setRepFiles(null);
                loadReports(orgId);
              }}>
              оновити
            </button>
          )}
        </div>
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
          Подана та прийнята звітність з Електронного кабінету ДПС (з квитанціями).
          {!orgId ? ' Оберіть юрособу.' : ' «Показати» — тягне з ДПС за період; '
            + '«оновити» — наново (якщо документ прийшов із битою кодировкою).'}
        </p>
        {reports !== null && (
          reports.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              За цей період поданої звітності немає.
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                  <th style={{ padding: '0.35rem', width: 110 }}>Форма</th>
                  <th style={{ padding: '0.35rem', width: 150 }}>Дата і час</th>
                  <th style={{ padding: '0.35rem' }}>Назва</th>
                  <th style={{ padding: '0.35rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.codRegdoc} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '0.35rem' }}>{r.doc ?? '—'}</td>
                    <td style={{ padding: '0.35rem' }}>{regDate(r)}</td>
                    <td style={{ padding: '0.35rem' }}>{regDocName(r)}</td>
                    <td style={{ padding: '0.35rem', textAlign: 'right' }}>
                      <button style={{ padding: '0.15rem 0.55rem', fontSize: '0.78rem' }}
                        disabled={privBusy}
                        onClick={() => orgId && openReportFiles(r, orgId)}>
                        {privBusy ? '…' : 'файли та квитанції'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
      )}

      {repFiles && (
        <ModalOverlay onClose={() => setRepFiles(null)} wide>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
            marginBottom: '0.6rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{repFiles.title}</span>
            <button style={{ marginLeft: 'auto', padding: '0.1rem 0.5rem',
              fontSize: '0.75rem' }}
              onClick={() => setRepFiles(null)}>закрити</button>
          </div>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: 0 }}>
            Основний звіт, додатки та квитанції — кожен окремим файлом.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
            <tbody>
              {repFiles.items.map((f) => (
                <tr key={f.codRegdoc} style={{ borderTop: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '0.3rem' }} title={f.doc ?? undefined}>
                    {regDocName(f)}
                  </td>
                  <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                    <button style={{ padding: '0.15rem 0.5rem', fontSize: '0.78rem' }}
                      disabled={privBusy}
                      onClick={() => downloadRegDoc(f)}>
                      {privBusy ? '…' : 'переглянути PDF'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ModalOverlay>
      )}

      {cabView && (
        <ModalOverlay
          onClose={() => {
            if (cabView.pdfUrl && !cabView.keepUrl) URL.revokeObjectURL(cabView.pdfUrl);
            setCabView(null);
          }}
          wide
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
            marginBottom: '0.6rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{cabView.title}</span>
            {cabView.pdfUrl && (
              <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '0.4rem' }}>
                <button
                  style={{ padding: '0.15rem 0.6rem', fontSize: '0.78rem' }}
                  title="Відкрити у повноцінному перегляді браузера (де шрифти ДПС коректні)"
                  onClick={() => window.open(cabView.pdfUrl!, '_blank')}>
                  відкрити
                </button>
                <button className="primary"
                  style={{ padding: '0.15rem 0.6rem', fontSize: '0.78rem' }}
                  title="Зберегти PDF на комп'ютер"
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = cabView.pdfUrl!;
                    a.download = `${cabView.title}.pdf`;
                    a.click();
                  }}>зберегти</button>
                <button
                  style={{ padding: '0.15rem 0.6rem', fontSize: '0.78rem' }}
                  onClick={() => {
                    const f = document.getElementById(
                      'dps-doc-frame') as HTMLIFrameElement | null;
                    f?.contentWindow?.print();
                  }}>друкувати</button>
              </span>
            )}
            <button style={{ marginLeft: cabView.pdfUrl ? 0 : 'auto',
              padding: '0.1rem 0.5rem', fontSize: '0.75rem' }}
              onClick={() => {
                if (cabView.pdfUrl && !cabView.keepUrl) URL.revokeObjectURL(cabView.pdfUrl);
                setCabView(null);
              }}>закрити</button>
          </div>
          {cabView.pdfUrl && (
            <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0 0 0.4rem' }}>
              Якщо у перегляді нижче замість тексту «####» — відкрийте документ
              кнопкою «відкрити» (там шрифти ДПС відображаються коректно).
            </p>
          )}
          {cabView.pdfUrl ? (
            <iframe id="dps-doc-frame" title={cabView.title} src={cabView.pdfUrl}
              style={{ width: '100%', height: '72vh', border: '1px solid #e2e8f0',
                borderRadius: 6 }} />
          ) : (
            <div style={{ fontSize: '0.9rem', lineHeight: 1.6,
              background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: 6,
              maxHeight: '70vh', overflow: 'auto' }}
              // HTML з ДПС (стан розрахунків) — довірений відправник.
              dangerouslySetInnerHTML={{ __html: cabView.html ?? '' }} />
          )}
        </ModalOverlay>
      )}

      {receiptView && (
        <ModalOverlay onClose={() => setReceiptView(null)} wide>
          <div className="print-hide" style={{ display: 'flex', alignItems: 'center',
            gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>
              Квитанція № {receiptView.fields.num}
            </span>
            {receiptView.fields.accepted === true && (
              <span style={{ color: '#166534', fontWeight: 600 }}>✓ прийнято</span>
            )}
            {receiptView.fields.accepted === false && (
              <span style={{ color: '#b91c1c', fontWeight: 600 }}>✗ не прийнято</span>
            )}
            <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>
              {receiptView.org}
            </span>
            <button className="primary"
              style={{ marginLeft: 'auto', padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
              onClick={printReceipt}>зберегти в PDF / друк</button>
            <button style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
              onClick={() => setReceiptView(null)}>закрити</button>
          </div>
          <div className="dps-receipt-print-root">
            <DpsReceiptBlank f={receiptView.fields} />
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}

// --- Іконки дій (inline SVG, stroke-стиль; без зовнішніх залежностей) ---
const svgProps = {
  width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.8,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
/** Підписати — перо (олівець-підпис). */
const IconSign = () => (
  <svg {...svgProps}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);
/** Відправити — паперовий літачок. */
const IconSend = () => (
  <svg {...svgProps}>
    <path d="m22 2-7 20-4-9-9-4Z" />
    <path d="M22 2 11 13" />
  </svg>
);
/** Оновити статус — кругова стрілка. */
const IconRefresh = () => (
  <svg {...svgProps}>
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <path d="M21 3v6h-6" />
  </svg>
);
/** Видалити — кошик. */
const IconTrash = () => (
  <svg {...svgProps}>
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

/** Кнопка-іконка у колонці «Дія»: тільки іконка + tooltip (Закон дизайну #7
 *  забороняє емодзі у кнопках-діях; SVG-іконка з підказкою — не емодзі). */
function IconBtn({ label, hint, color, disabled, busy, onClick, children }: {
  label: string;
  hint?: string;
  color?: string;
  disabled?: boolean;
  busy?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={hint ?? label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 26, padding: 0, border: '1px solid transparent',
        borderRadius: 6, background: 'transparent',
        color: disabled ? '#cbd5e1' : (color ?? '#475569'),
        cursor: disabled ? 'default' : 'pointer',
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = '#f1f5f9';
        e.currentTarget.style.borderColor = '#e2e8f0';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.borderColor = 'transparent';
      }}
    >
      {busy && disabled ? '…' : children}
    </button>
  );
}

/** Бейдж квитанції №1/№2: активний (є квитанція) → клікабельний бланк;
 *  сірий (немає) → неактивний. */
function Kvt(
  { n, active, clickable, busy, onClick }: {
    n: 1 | 2; active: boolean; clickable: boolean; busy: boolean;
    onClick: () => void;
  },
) {
  const base: React.CSSProperties = {
    padding: '0.12rem 0.45rem', fontSize: '0.72rem', borderRadius: 4,
    border: '1px solid', fontWeight: 600, lineHeight: 1.3,
  };
  if (!active) {
    return (
      <span style={{ ...base, color: '#94a3b8', borderColor: '#e2e8f0',
        background: '#f8fafc' }} title={`Квитанції №${n} ще немає`}>
        кв.№{n}
      </span>
    );
  }
  return (
    <button style={{ ...base, color: '#1d4ed8', borderColor: '#bfdbfe',
      background: '#eff6ff', cursor: clickable ? 'pointer' : 'not-allowed',
      opacity: clickable ? 1 : 0.6 }}
      disabled={!clickable || busy}
      title={clickable ? `Відкрити квитанцію №${n} (бланк, PDF)`
        : 'Зчитайте ключ КЕП, щоб відкрити квитанцію'}
      onClick={onClick}>
      {busy ? '…' : `кв.№${n}`}
    </button>
  );
}

/**
 * Проста модалка-оверлей: затемнений фон + біла картка по центру.
 * Клік по фону / Esc — закриває. Скрол — усередині картки.
 */
function ModalOverlay(
  { children, onClose, wide = false }: {
    children: ReactNode; onClose: () => void; wide?: boolean;
  },
) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="print-hide-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: wide ? 900 : 720, width: '100%', maxHeight: '85vh',
          overflow: 'auto', background: '#fff', borderRadius: 8,
          padding: '1rem 1.25rem', boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        }}
      >
        {children}
      </div>
    </div>
  );
}
